import React from 'react';
import {useIntl} from 'react-intl';
import {Button, EMPHASIS, SubHeading, Text} from '@forecast-it/design-system';
import {QuickbooksIcon, SIZE} from '@/web-components';
import InlineLoader from '../../../shared/components/inline-loader/inline_loader';
import {
	Actions,
	ConfigPageCard,
	Intro,
	IntroText,
	OrganizationRow,
	OrganizationsHeading,
	RowActions,
} from '../xero/XeroConfigPageStyled';

export type ConnectedQuickbooksCompany = {name?: string; realmId?: string; inaccessible?: boolean};

type Props = {
	organizations: ReadonlyArray<ConnectedQuickbooksCompany>;
	// quickbooks_multi_tenant: several organisations, each removed by realmId, with Add organization.
	multiTenant: boolean;
	// company.quickbooksEnabled
	connected: boolean;
	fetching: boolean;
	onAdd: () => void;
	onReconnect: () => void;
	onRemove: (realmId?: string) => void;
	onHelp: () => void;
};

/**
 * The intro and the connected-organisation list of Admin > Integrations > QuickBooks in the new design,
 * the QuickBooks copy of XeroOrganizationsSection (T4-205); both are built from XeroConfigPageStyled.
 * Presentational only: every action is the page's own handler, passed in, so connecting, reconnecting,
 * removing and the help link behave exactly as on the legacy page.
 *
 * Multi-tenant on: as Xero - a row per organisation (Reconnect when inaccessible, Remove by realmId),
 * then Add organization. Multi-tenant off: one connection, as today - connected shows that company with
 * Remove (today's Disable, no realmId), plus Reconnect when it is inaccessible, and no Add; not
 * connected keeps Intuit's own Connect button.
 */
export const QuickbooksOrganizationsSection = ({
	organizations,
	multiTenant,
	connected,
	fetching,
	onAdd,
	onReconnect,
	onRemove,
	onHelp,
}: Props) => {
	const {formatMessage} = useIntl();
	// One string with a blank line between paragraphs in English; other languages may have one paragraph.
	const paragraphs = formatMessage({id: 'settings.app_catalog.quickbooks.description'}).split('\n\n');

	// The single connection's row is the page's only Remove, so it must not depend on the companies call
	// answering: with no company to name, it reads as a label rather than a made-up company name.
	const singleConnection = organizations[0];
	const singleConnectionName =
		(singleConnection && (singleConnection.name || singleConnection.realmId)) ||
		formatMessage({id: 'settings.app_catalog.quickbooks.connected_company_fallback'});

	// Single-connection mode with nothing connected has no organisation to list, only Intuit's Connect.
	const showOrganizationsHeading = multiTenant || connected;

	const renderReconnect = () => (
		<Button emphasis={EMPHASIS.MEDIUM} onPress={onReconnect} data-cy="qbo-organization-reconnect">
			{formatMessage({id: 'common.reconnect'})}
		</Button>
	);

	const renderRemove = (realmId?: string) => (
		<Button emphasis={EMPHASIS.MEDIUM} onPress={() => onRemove(realmId)} data-cy="qbo-organization-remove">
			{formatMessage({id: 'common.remove'})}
		</Button>
	);

	const helpButton = (
		<Button emphasis={EMPHASIS.LOW} onPress={onHelp} data-cy="qbo-help-center">
			{formatMessage({id: 'settings.app_catalog.help_button'})}
		</Button>
	);

	const renderMultiTenant = () => (
		<>
			{organizations.map((organization, index) => (
				// realmId is the key; the index only covers a company the list returned without one.
				<OrganizationRow key={organization.realmId || `no-realm-${index}`} data-cy="qbo-organization-row">
					<SubHeading size="1">{organization.name || organization.realmId}</SubHeading>
					<RowActions>
						{organization.inaccessible ? renderReconnect() : null}
						{/* Remove by realmId only: without one, disableQuickbooks(undefined) would run the
						    single-connection disable instead of removing this organisation. */}
						{organization.realmId ? renderRemove(organization.realmId) : null}
					</RowActions>
				</OrganizationRow>
			))}
			<Actions>
				<Button emphasis={EMPHASIS.HIGH} onPress={onAdd} data-cy="qbo-add-organization">
					{formatMessage({id: 'settings.app_catalog.xero.add_organization'})}
				</Button>
				{helpButton}
			</Actions>
		</>
	);

	const renderSingleConnection = () => (
		<>
			{connected ? (
				<OrganizationRow data-cy="qbo-organization-row">
					<SubHeading size="1">{singleConnectionName}</SubHeading>
					<RowActions>
						{/* The import card asks the admin to reconnect an inaccessible company "in the list
						    above"; this is that button, running today's connect flow. */}
						{singleConnection && singleConnection.inaccessible ? renderReconnect() : null}
						{renderRemove()}
					</RowActions>
				</OrganizationRow>
			) : null}
			<Actions>
				{connected ? null : (
					// Intuit's own Connect to QuickBooks button, kept exactly as today (app_quickbooks.scss).
					<div className="qb-buttons">
						<button className="connect-quickbooks" onClick={onAdd} data-cy="qbo-connect" />
					</div>
				)}
				{helpButton}
			</Actions>
		</>
	);

	return (
		<ConfigPageCard className="qbo-config-page-card" data-cy="qbo-organizations-section">
			<Intro>
				<QuickbooksIcon size={SIZE.EXTRA_BIG} />
				<IntroText>
					{/* A static split of one string: the index is a stable key, and text could repeat. */}
					{paragraphs.map((paragraph, index) => (
						<Text key={index} size="2">
							{paragraph}
						</Text>
					))}
				</IntroText>
			</Intro>

			{showOrganizationsHeading ? (
				<OrganizationsHeading>
					<SubHeading size="2">{formatMessage({id: 'common.organization'})}</SubHeading>
				</OrganizationsHeading>
			) : null}

			{fetching ? <InlineLoader /> : multiTenant ? renderMultiTenant() : renderSingleConnection()}
		</ConfigPageCard>
	);
};
